import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, FileText, Download, Clock } from "lucide-react";
import { Entity360Shell } from "../components/shared/OSComponents";
import EntityNotes from "../components/shared/EntityNotes";
import { usePageNavigate } from "../hooks/usePageNavigate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Report360Page({ reportId, onBack, breadcrumbs = [] }) {
  const { navigateToEntity } = usePageNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/entity/preview/report/${reportId}`, { headers })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [reportId]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>;

  const crumbs = [...breadcrumbs, { type: "report", id: reportId, label: `Report ${reportId?.slice(0, 8)}` }];

  const kpis = [
    data?.report_type && { label: "Report Type", value: data.report_type },
    data?.entity_type && { label: "Entity Type", value: data.entity_type },
    data?.status && { label: "Status", value: data.status },
    data?.created_at && { label: "Created", value: new Date(data.created_at).toLocaleDateString() },
  ].filter(Boolean);

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "notes", label: "Notes" },
  ];

  const quickActions = data?.download_url ? [
    { label: "Download Report", icon: Download, onClick: () => window.open(data.download_url, "_blank") },
  ] : [];

  return (
    <Entity360Shell
      type="document"
      title={data?.title || `Report ${reportId?.slice(0, 8)}`}
      subtitle={data?.created_at ? `Generated ${new Date(data.created_at).toLocaleDateString()}` : undefined}
      status={data?.status}
      kpis={kpis}
      tabs={tabs}
      activeTab={tab}
      onTabChange={setTab}
      onBack={onBack}
      onNavigate={navigateToEntity}
      breadcrumbs={crumbs}
      quickActions={quickActions}
      relatedEntities={[]}
    >
      {tab === "overview" && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-4 sm:p-6" data-testid="report-overview">
          {data ? (
            <div className="space-y-4">
              {data.summary && (
                <div className="p-4 rounded-lg bg-[#faf7f2] border border-[#e8e0d0]">
                  <div className="text-[9px] text-[#999] uppercase mb-2">Summary</div>
                  <p className="text-xs text-[#555] leading-relaxed">{data.summary}</p>
                </div>
              )}
              {!data.summary && (
                <p className="text-sm text-[#888] text-center py-8">No summary available for this report.</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-[#888] text-center py-8">Report data not available.</p>
          )}
        </div>
      )}
      {tab === "notes" && <EntityNotes entityType="report" entityId={reportId} />}
    </Entity360Shell>
  );
}
